import type {
	ImportFileMappingFormData,
	ImportFromFileContextValue,
	ImportFromFileFormData,
} from '@/shared/components/ImportFromFileModal'
import type {
	FilterRuleModel,
	HTTPValidationError,
	OptimizationActionResponseModel,
} from '@/shared/generated'

import { RiAlertFill, RiInformationFill } from '@remixicon/react'
import { AxiosError } from 'axios'
import { uniq } from 'lodash/fp'
import { AlertTriangleIcon, ChevronLeftIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useColumnsSchemaQuery } from '@/entities/column'
import {
	FINAL_FIELDS,
	useBulkFinalFieldsFileMutation,
} from '@/entities/optimization'

import { client } from '@/shared/api/client'
import { ValidationErrorsList } from '@/shared/components/common/ValidationErrorsList'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import RHFFileDropArea from '@/shared/components/HookForm/RHFFileDropArea'
import {
	ImportFromFileForm,
	ImportFromFileFormDescription,
	ImportFromFileMapping,
	ImportFromFileProcessing,
	ImportFromFileProvider,
	useImportFromFile,
} from '@/shared/components/ImportFromFileModal'
import { Button } from '@/shared/components/ui/button'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import {
	Item,
	ItemContent,
	ItemDescription,
	ItemMedia,
} from '@/shared/components/ui/item'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Separator } from '@/shared/components/ui/separator'
import { HiddenOnContext } from '@/shared/generated'
import { downloadProductsImportTemplate } from '@/shared/lib/download-xlsx'

import { OptimizationActionsResponseList } from './OptimizationActionsResponseList'

type ProductsImportModalProps = {
	filters: FilterRuleModel[]
	close(): void
	isOpen: boolean
}

const ImportFromFileModalContent = ({
	bulkFinalFieldsFile,
}: {
	bulkFinalFieldsFile: ReturnType<typeof useBulkFinalFieldsFileMutation>
}) => {
	const { t } = useTranslation()

	const { step } = useImportFromFile()
	const handleDownloadTemplate = async () => {
		const columns = await client.fieldSchema.fieldSchemaList({
			context: [HiddenOnContext.Assortment],
		})
		downloadProductsImportTemplate(
			uniq([
				'SKU',
				...columns
					.filter((column) => column.params.editable)
					.map((column) =>
						t(column.name, { ns: 'fields', defaultValue: column.name })
					),
			])
		)
	}

	return (
		<>
			{step === 'form' && (
				<ImportFromFileForm>
					<RHFFileDropArea name='product_diffs' />
					<Separator />
					<ImportFromFileFormDescription
						onDownloadTemplate={handleDownloadTemplate}
					/>
				</ImportFromFileForm>
			)}
			{step === 'mapping' && <ImportFromFileMapping />}
			{step === 'processing' && <ImportFromFileProcessing />}
			{step === 'errors' && (
				<ImportFromFileError error={bulkFinalFieldsFile.error} />
			)}
			{step === 'success' && (
				<ImportFromFilePartiallySuccessful data={bulkFinalFieldsFile.data!} />
			)}
		</>
	)
}

export function AssortmentImportModal({
	close,
	isOpen,
	filters,
}: ProductsImportModalProps) {
	const { t } = useTranslation()
	const columnsSchemaQuery = useColumnsSchemaQuery<
		{
			label: string
			value: string
		}[]
	>(HiddenOnContext.Products, {
		select: (data) =>
			data
				?.filter(
					(column) =>
						(column.params.editable && FINAL_FIELDS.includes(column.id)) ||
						column.id === 'sku'
				)
				?.map((column) => ({
					label: t(column.name, { ns: 'fields', defaultValue: column.name }),
					value: column.id,
				})) || [],
	})
	const bulkFinalFieldsFile = useBulkFinalFieldsFileMutation()
	const onSubmit = async (
		data: ImportFromFileFormData & ImportFileMappingFormData,
		context: ImportFromFileContextValue
	) => {
		context.setStep('processing')
		try {
			await bulkFinalFieldsFile.mutateAsync({
				final_fields_file: data.product_diffs[0],
				filters: JSON.stringify(filters),
				file_header_mapping: JSON.stringify(data.file_header_mapping),
			})
			context.setStep('success')
		} catch (error) {
			context.setStep('errors')
		}
	}

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ImportFromFileProvider
				onSubmit={onSubmit}
				schemaOptions={columnsSchemaQuery?.data || []}
			>
				<ModalContent data-testid='products-import-modal'>
					<ModalHeader>
						<ModalTitle>{t('modal_import_title')}</ModalTitle>
					</ModalHeader>
					<ErrorBoundary>
						<ImportFromFileModalContent
							bulkFinalFieldsFile={bulkFinalFieldsFile}
						/>
					</ErrorBoundary>
				</ModalContent>
			</ImportFromFileProvider>
		</Modal>
	)
}

const ImportFromFilePartiallySuccessful = ({
	data,
}: {
	data: OptimizationActionResponseModel[]
}) => {
	const { t } = useTranslation()

	const isFullySuccessful = data.every((item) => item.success)
	const context = useImportFromFile()

	return (
		<>
			<ModalBody className='relative flex min-h-75 flex-col overflow-hidden'>
				{!data?.length && (
					<Item size='xs' variant='muted'>
						<ItemMedia variant='icon'>
							<RiAlertFill className='text-yellow-500' />
						</ItemMedia>
						<ItemContent>
							<ItemDescription>
								{t('import.no_changes_applied', {
									defaultValue: 'No changes were applied to any optimizations.',
								})}
							</ItemDescription>
						</ItemContent>
					</Item>
				)}
				<div className='mb-auto h-72 w-full scroll-fade overflow-y-auto'>
					<OptimizationActionsResponseList data={data} />
				</div>

				{isFullySuccessful && (
					<Item size='xs' variant='muted' className='bg-green-500/10'>
						<ItemMedia variant='icon'>
							<RiInformationFill className='text-green-500' />
						</ItemMedia>
						<ItemContent>
							<ItemDescription>
								{t('import.fully_success.description', {
									defaultValue:
										'Your changes will be applied in the background. I could take up to 1 minute to complete.',
								})}
							</ItemDescription>
						</ItemContent>
					</Item>
				)}
				{!isFullySuccessful && (
					<Item size='xs' variant='muted' className='bg-yellow-500/10'>
						<ItemMedia variant='icon'>
							<RiAlertFill className='text-yellow-500' />
						</ItemMedia>
						<ItemContent>
							<ItemDescription>
								{t('import.partially_success.description', {
									defaultValue:
										"Some optimizations were not updated successfully. Bulk update is only applicable to OGs in 'Finished' status. Please check the logs for more details.",
								})}
							</ItemDescription>
						</ItemContent>
					</Item>
				)}
			</ModalBody>
			<ModalFooter className='justify-start'>
				<Button onClick={() => context.setStep('form')}>
					<ChevronLeftIcon />
					{t('general_back')}
				</Button>
				<ModalClose asChild>
					<Button variant='primary'>{t('general_close')}</Button>
				</ModalClose>
			</ModalFooter>
		</>
	)
}
const ImportFromFileError = ({
	error = null,
}: {
	error: AxiosError<HTTPValidationError> | null
}) => {
	const { t } = useTranslation()
	const context = useImportFromFile()
	if (error instanceof AxiosError) {
		if (error.response?.status === 422) {
			const errors = error?.response?.data?.detail || []

			return (
				<>
					<ModalBody className='relative flex min-h-75 flex-col gap-2 overflow-hidden'>
						<div className='mb-auto flex w-full flex-col gap-2'>
							<ValidationErrorsList errors={errors} />
						</div>
						<Item size='xs' variant='muted'>
							<ItemMedia variant='icon'>
								<RiAlertFill className='text-yellow-500' />
							</ItemMedia>
							<ItemContent>
								<ItemDescription>
									{t('import.validation_errors.description', {
										defaultValue:
											'Some validation errors occurred. Please check the logs for more details.',
									})}
								</ItemDescription>
							</ItemContent>
						</Item>
					</ModalBody>
					<ModalFooter className='justify-start'>
						<Button onClick={() => context.setStep('form')}>
							<ChevronLeftIcon />
							{t('general_back')}
						</Button>
						<ModalClose asChild>
							<Button variant='primary'>{t('general_close')}</Button>
						</ModalClose>
					</ModalFooter>
				</>
			)
		}
	}

	return (
		<>
			<ModalBody className='relative flex min-h-75 flex-col overflow-hidden'>
				<Empty>
					<EmptyHeader>
						<EmptyMedia variant='icon' className='bg-destructive/20'>
							<AlertTriangleIcon className='text-destructive' />
						</EmptyMedia>
						<EmptyTitle>{t('og_bulk_import_error')}</EmptyTitle>
						<EmptyDescription>{t('og_bulk_import_canceled')}</EmptyDescription>
					</EmptyHeader>

					<EmptyContent>
						<div className='prose prose-sm grid w-full rounded-md bg-muted/30 p-2 text-left text-xs prose-neutral dark:prose-invert'>
							<h5>{t('general_possible_reasons')}:</h5>
							<div
								dangerouslySetInnerHTML={{
									__html: t('og_import_error_hint_html'),
								}}
							/>
						</div>
					</EmptyContent>
				</Empty>
			</ModalBody>
			<ModalFooter className='justify-start'>
				<Button onClick={() => context.setStep('form')}>
					<ChevronLeftIcon />
					{t('general_back')}
				</Button>
				<ModalClose asChild>
					<Button variant='primary'>{t('general_close')}</Button>
				</ModalClose>
			</ModalFooter>
		</>
	)
}
